import { createFileRoute, useParams } from "@tanstack/react-router";
import { BackToDashboard } from "@/components/BackToDashboard";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { getInvoice, resendInvoiceEmail, type InvoiceLineItem } from "@/lib/invoices.functions";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/invoices/$invoiceId")({
  head: () => ({ meta: [{ title: "Invoice — The Hospitality Reserve" }] }),
  component: InvoicePage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">Could not load invoice: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8 text-sm">Invoice not found.</div>,
});

function fmtMoney(p: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(p / 100);
}

function InvoicePage() {
  const { invoiceId } = useParams({ from: "/_authenticated/invoices/$invoiceId" });
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => getInvoice({ data: { id: invoiceId } }),
  });
  const resend = useServerFn(resendInvoiceEmail);
  const [resending, setResending] = useState(false);
  const onResend = async () => {
    setResending(true);
    try {
      await resend({ data: { id: invoiceId } });
      toast.success("A fresh copy has been emailed to you.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not resend.");
    } finally {
      setResending(false);
    }
  };

  if (isLoading) return <div className="p-8 text-sm">Loading…</div>;
  if (error || !data || "error" in data) {
    return <div className="p-8 text-sm text-destructive">Could not load invoice.</div>;
  }
  const inv = data;
  const items = (inv.line_items ?? []) as InvoiceLineItem[];
  const issuer = inv.type.startsWith("venue")
    ? { name: "The Hospitality Reserve", line: "Issued by The Hospitality Reserve · London" }
    : { name: "The Hospitality Reserve (self-billing)", line: "Self-billing invoice issued on contractor's behalf" };

  return (
    <div className="min-h-screen bg-white text-black">
      <div className="mx-auto max-w-3xl p-8 print:p-0">
        <div className="mb-6 flex items-center justify-between gap-2 print:hidden">
          <BackToDashboard />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onResend} disabled={resending}>
              {resending ? "Sending…" : "Email me a copy"}
            </Button>
            <Button variant="outline" onClick={() => window.print()}>Print / Save as PDF</Button>
          </div>
        </div>


        <div className="border border-black/10 p-10 shadow-sm print:border-0 print:shadow-none">
          <header className="mb-10 flex items-start justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-black/50">The Hospitality Reserve</p>
              <p className="mt-1 text-2xl font-light">Invoice</p>
              <p className="mt-1 text-xs text-black/60">{issuer.line}</p>
            </div>
            <div className="text-right text-xs text-black/70">
              <p className="font-mono">{inv.invoice_number}</p>
              <p className="mt-1">Issued {new Date(inv.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
            </div>
          </header>

          <section className="mb-8 grid grid-cols-2 gap-8 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-black/50">Bill to</p>
              <p className="mt-1 font-medium">{inv.recipient_name}</p>
              {inv.recipient_address && <p className="text-black/70">{inv.recipient_address}</p>}
              {inv.recipient_postcode && <p className="text-black/70">{inv.recipient_postcode}</p>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-black/50">From</p>
              <p className="mt-1 font-medium">{issuer.name}</p>
              <p className="text-black/70">London &amp; the Home Counties</p>
              {inv.counterparty_name && (
                <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-black/50">Re. counterparty</p>
              )}
              {inv.counterparty_name && <p className="text-black/70">{inv.counterparty_name}</p>}
            </div>
          </section>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-black/15 text-[10px] uppercase tracking-[0.25em] text-black/50">
                <th className="py-3 text-left">Description</th>
                <th className="py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((li, i) => (
                <tr key={i} className="border-b border-black/10">
                  <td className="py-3 pr-4">{li.description}</td>
                  <td className="py-3 text-right">{fmtMoney(li.amount_pence)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {inv.vat_pence > 0 && (
                <tr>
                  <td className="py-2 text-right text-xs text-black/60">VAT</td>
                  <td className="py-2 text-right text-xs">{fmtMoney(inv.vat_pence)}</td>
                </tr>
              )}
              <tr>
                <td className="pt-4 text-right text-[11px] uppercase tracking-[0.25em] text-black/50">Total</td>
                <td className="pt-4 text-right text-lg font-medium">{fmtMoney(inv.total_pence)}</td>
              </tr>
            </tfoot>
          </table>

          {inv.notes && (
            <p className="mt-10 border-t border-black/10 pt-6 text-xs leading-relaxed text-black/60">
              {inv.notes}
            </p>
          )}
          <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-black/40">
            The Hospitality Reserve is a neutral technology platform. It is not an employer or employment business.
          </p>
        </div>
      </div>
      <style>{`@media print { body { background: white; } }`}</style>
    </div>
  );
}
